import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { InputFieldComponent } from "../../../../components/form/input-field/input-field.component";
import { SelectDepartmentFieldComponent } from "../../../../components/form/selects/select-department-field/select-department-field.component";
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { SelectUserStatusFieldComponent } from '../../../../components/options/select-user-status-field/select-user-status-field.component';
import { User } from '../../../../models/entities/users';
import { ValidatorService } from '../../../../services/validator.service';
import { FormService } from '../../../../services/form.service';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AlertInfo } from '../../../../models/alert-info';
import { UserService } from '../../../../services/api/user.service';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'users-form',
  imports: [CommonModule, ReactiveFormsModule, InputFieldComponent, TextAreaFieldComponent, SelectUserStatusFieldComponent, SelectDepartmentFieldComponent, NgbAlert],
  templateUrl: './users-form.component.html',
  styleUrl: './users-form.component.scss'
})
export class UsersFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user?: User;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();
  onGlobalLab: boolean = false

  form!: FormGroup;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private userService: UserService,
    private alertNotificationService: AlertNotificationService,
    private route: ActivatedRoute,
  ) {

  }


  ngOnInit(): void {
    const userId = this.route.snapshot.paramMap.get('userId');

    let passwordValidators = userId
      ? [this.validatorService.passwordValidator()]
      : [Validators.required, this.validatorService.passwordValidator()];

    this.form = new FormGroup({
      userId: new FormControl({value:'', disabled: true}, Validators.required),
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, this.validatorService.emailValidator()]),
      password: new FormControl('', passwordValidators),
      repeatPassword: new FormControl('', userId ? [] : [Validators.required]),
      mobilePhone: new FormControl(''),
      msTeamsName: new FormControl(''),
      department: new FormControl(''),
      ranking: new FormControl(''),
      notes: new FormControl(''),
      status: new FormControl('', [Validators.required]),
      timezone: new FormControl('')
    }, { validators: this.customPasswordMatchValidator });

    if (this.user?.departmentId) {
      this.onGlobalLab = true
    }
    
    this.form.valueChanges.subscribe(() => {
      const password = this.form.get('password');
      const repeatPassword = this.form.get('repeatPassword');
      
      if (this.form.hasError('passwordMismatch') && repeatPassword && repeatPassword.value) {
        repeatPassword.setErrors({...repeatPassword.errors, passwordMismatch: true});
      } else if (repeatPassword && repeatPassword.errors && repeatPassword.errors['passwordMismatch']) {
        const errors = {...repeatPassword.errors};
        delete errors['passwordMismatch'];
        repeatPassword.setErrors(Object.keys(errors).length ? errors : null);
      }
    });
  }

  private customPasswordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const password = group.get('password')?.value;
    const repeatPassword = group.get('repeatPassword')?.value;

    if (this.user?.userId && !password) {
      return null;
    }

    return password === repeatPassword ? null : { passwordMismatch: true };
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['user'] && changes['user'].currentValue) {
      this.form.patchValue({
        userId: this.user?.userId,
        firstName: this.user?.firstName,
        lastName: this.user?.lastName,
        email: this.user?.email,
        mobilePhone: this.user?.mobilePhone,
        msTeamsName: this.user?.teamsId,
        departmentId: this.user?.departmentId,
        ranking: this.user?.ranking,
        notes: this.user?.notes,
        status: this.user?.status,
        timezone: this.user?.timezone
      });
      if (this.user?.departmentId) {
        this.onGlobalLab = true
      }
    }
  }
  ngOnDestroy(): void {
    this.onGlobalLab = false
  }


  /**
   * *******************
   * Submit form methods
   * *******************
   */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  submitForm(): void {

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (this.form.valid) {

      const formValues = { ...this.form.value };

      if (formValues.password === '') {
        delete formValues.password;
        delete formValues.repeatPassword;
      }
      console.log(formValues);
      const values = Object.assign(
        formValues,
        {
          tenantId: 1,
        },
      );

      console.log(values);

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const serviceMethod = this.user ? this.userService.update(this.user.userId, values) : this.userService.create(values);
      serviceMethod.subscribe({
        next: (response: any) => {
          this.submitFormSuccess(response)
        },
        error: (error) => {
          this.submitFormError(error)
        }
      })
    }
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */


  getUserIdInput(): FormControl { return this.getFormControl('userId'); }
  getFirstNameInput(): FormControl { return this.getFormControl('firstName'); }
  getLastNameInput(): FormControl { return this.getFormControl('lastName'); }
  getEmailInput(): FormControl { return this.getFormControl('email'); }
  getPasswordInput(): FormControl { return this.getFormControl('password'); }
  getRepeatPasswordInput(): FormControl { return this.getFormControl('repeatPassword'); }
  getMobilePhoneInput(): FormControl { return this.getFormControl('mobilePhone'); }
  getTeamsIdInput(): FormControl { return this.getFormControl('msTeamsName'); }
  getDepartmentSelect(): FormControl { return this.getFormControl('department'); }
  getRankingInput(): FormControl { return this.getFormControl('ranking'); }
  getNotesTextArea(): FormControl { return this.getFormControl('notes'); }
  getStatusSelect(): FormControl { return this.getFormControl('status'); }
  getTimezoneSelect(): FormControl { return this.getFormControl('timezone'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }
}
